import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc, getDoc, addDoc, deleteField, writeBatch, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { PlusCircle, Calendar, Copy, BarChart } from 'lucide-react';
import ProductSelector from '../components/advertising/ProductSelector';
import SponsorshipConfig from '../components/advertising/SponsorshipConfig';
import StripeAccountMessage from '../components/advertising/StripeAccountMessage';
import ProductStats from '../components/advertising/ProductStats';

interface SponsorshipConfigType {
  sponsored: boolean;
  sponsorshipStartDate: string;
  sponsorshipEndDate: string;
  pricingType: 'click' | 'sale';
  pricingValue: number;
}

interface CampaignStats {
  views: number;
  clicks: number;
  sales: number;
  cost: number;
  paidAmount?: number;
  nextPaymentDate?: string;
  amountToPay?: number;
}

interface Campaign {
  id?: string;
  name: string;
  products: string[];
  companyId: string;
  companyName?: string;
  createdAt: string;
  status: 'active' | 'draft' | 'ended' | 'paused' | 'deleted';
  sponsored: boolean;
  sponsorshipStartDate: string;
  sponsorshipEndDate: string;
  pricingType: 'click' | 'sale';
  pricingValue: number;
  nextPaymentDate?: string;
  stats?: CampaignStats;
}

export default function Advertising() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [showCreateFlow, setShowCreateFlow] = useState(false);
  const [step, setStep] = useState(1);
  const [campaignName, setCampaignName] = useState('');
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [sponsorshipConfig, setSponsorshipConfig] = useState<any>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [activeCampaigns, setActiveCampaigns] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [isEditingProducts, setIsEditingProducts] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [trackingCode, setTrackingCode] = useState('');
  const [unsubscribe, setUnsubscribe] = useState<(() => void) | null>(null);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [loadingStripe, setLoadingStripe] = useState(true);
  const [selectedCampaignForStats, setSelectedCampaignForStats] = useState<string | null>(null);

  useEffect(() => {
    console.log('Current user:', currentUser);
    console.log('Is admin:', currentUser?.role === 'admin');
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.companyId) {
      navigate('/login');
      return;
    }

    const campaignsRef = collection(db, 'campaigns');
    const q = query(
      campaignsRef,
      where('companyId', '==', currentUser.companyId),
      where('status', 'in', ['active', 'paused'])
    );

    const unsubscribeListener = onSnapshot(q, async (snapshot) => {
      try {
        setIsLoading(true);
        const campaigns: Campaign[] = [];
        const companyIds = new Set(snapshot.docs.map(doc => doc.data().companyId));
        const companiesData = new Map();

        for (const companyId of companyIds) {
          const companyRef = doc(db, 'companies', companyId);
          const companySnap = await getDoc(companyRef);
          if (companySnap.exists()) {
            companiesData.set(companyId, companySnap.data());
          }
        }

        for (const doc of snapshot.docs) {
          const campaign = { id: doc.id, ...doc.data() } as Campaign;
          const companyData = companiesData.get(campaign.companyId);
          
          if (companyData) {
            campaign.companyName = companyData.legalName || 'Empresa não encontrada';
          }

          const stats = await fetchCampaignStats(
            doc.id,
            dateRange.startDate,
            dateRange.endDate
          );

          campaigns.push({
            ...campaign,
            stats
          });
        }

        setActiveCampaigns(campaigns);
      } catch (err) {
        console.error('Erro ao processar campanhas:', err);
        setError('Erro ao processar campanhas');
      } finally {
        setIsLoading(false);
      }
    }, (error) => {
      console.error('Erro na subscrição:', error);
      setError('Erro ao monitorar campanhas');
      setIsLoading(false);
    });

    setUnsubscribe(() => unsubscribeListener);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [currentUser, dateRange]);

  const getNextFriday = () => {
    const today = new Date();
    const friday = new Date(today);
    friday.setDate(today.getDate() + ((7 + 5 - today.getDay()) % 7 || 7));
    friday.setHours(0, 0, 0, 0);
    return friday;
  };

  const initializeCampaignCollections = async (campaignId: string) => {
    // Only create the collection references without adding any documents
    const collections = ['views', 'clicks', 'sales', 'payments'];
    try {
      collections.forEach(collectionName => {
        collection(db, `campaigns/${campaignId}/${collectionName}`);
      });
    } catch (err) {
      console.error('Erro ao inicializar coleções:', err);
    }
  };

  const fetchCampaignStats = async (campaignId: string, startDate: string, endDate: string) => {
    try {
      // Initialize all counters to 0
      const stats = {
        views: 0,
        clicks: 0,
        sales: 0,
        cost: 0,
        paidAmount: 0,
        nextPaymentDate: '',
        amountToPay: 0
      };

      // Convert dates to start and end of day for comparison
      const startDateTime = new Date(startDate);
      startDateTime.setHours(0, 0, 0, 0);
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);

      // Get collection references
      const viewsRef = collection(db, `campaigns/${campaignId}/views`);
      const clicksRef = collection(db, `campaigns/${campaignId}/clicks`);
      const salesRef = collection(db, `campaigns/${campaignId}/sales`);
      const paymentsRef = collection(db, `campaigns/${campaignId}/payments`);

      // Create queries with date filters
      const viewsQuery = query(viewsRef, 
        where('timestamp', '>=', startDateTime.toISOString()),
        where('timestamp', '<=', endDateTime.toISOString())
      );
      const clicksQuery = query(clicksRef,
        where('timestamp', '>=', startDateTime.toISOString()),
        where('timestamp', '<=', endDateTime.toISOString())
      );
      const salesQuery = query(salesRef,
        where('timestamp', '>=', startDateTime.toISOString()),
        where('timestamp', '<=', endDateTime.toISOString())
      );
      const paymentsQuery = query(paymentsRef,
        where('timestamp', '>=', startDateTime.toISOString()),
        where('timestamp', '<=', endDateTime.toISOString()),
        where('status', '==', 'succeeded')
      );

      // Get snapshots
      const [viewsSnap, clicksSnap, salesSnap, paymentsSnap] = await Promise.all([
        getDocs(viewsQuery),
        getDocs(clicksQuery),
        getDocs(salesQuery),
        getDocs(paymentsQuery)
      ]);

      // Count documents within date range
      stats.views = viewsSnap.size;
      stats.clicks = clicksSnap.size;
      stats.sales = salesSnap.size;

      // Sum payments
      paymentsSnap.forEach(doc => {
        const payment = doc.data();
        if (payment.amount) {
          stats.paidAmount += Number(payment.amount);
        }
      });

      // Get campaign data
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      
      if (!campaignSnap.exists()) {
        console.error('Campaign not found');
        return stats;
      }

      const campaign = campaignSnap.data() as Campaign;
      
      // Calculate cost based on pricing type
      if (campaign.pricingType === 'click') {
        stats.cost = stats.clicks * campaign.pricingValue;
      } else if (campaign.pricingType === 'sale') {
        stats.cost = stats.sales * campaign.pricingValue;
      }

      // Set next payment date
      const nextFriday = getNextFriday();
      stats.nextPaymentDate = campaign.nextPaymentDate || nextFriday.toISOString();

      if (!campaign.nextPaymentDate || new Date(campaign.nextPaymentDate) < new Date()) {
        await updateDoc(campaignRef, {
          nextPaymentDate: nextFriday.toISOString()
        });
      }

      // Calculate amount to pay
      stats.amountToPay = Math.max(0, stats.cost - stats.paidAmount);

      return stats;

    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
      return {
        views: 0,
        clicks: 0,
        sales: 0,
        cost: 0,
        paidAmount: 0,
        nextPaymentDate: getNextFriday().toISOString(),
        amountToPay: 0
      };
    }
  };

  const handleCreateCampaign = async () => {
    if (!currentUser?.companyId) {
      setError('Erro: Usuário não está associado a uma empresa');
      return;
    }

    if (!campaignName.trim()) {
      setError('Por favor, insira um nome para a campanha');
      return;
    }

    if (selectedProducts.length === 0) {
      setError('Por favor, selecione pelo menos um produto');
      return;
    }

    if (!sponsorshipConfig) {
      setError('Por favor, configure os detalhes do patrocínio corretamente');
      return;
    }

    setIsCreating(true);
    setError('');

    try {
      // Verificar se os produtos selecionados existem
      const productChecks = await Promise.all(
        selectedProducts.map(async (productId) => {
          const productRef = doc(db, 'products', productId);
          const productSnap = await getDoc(productRef);
          return productSnap.exists();
        })
      );

      if (productChecks.some(exists => !exists)) {
        throw new Error('Um ou mais produtos selecionados não existem');
      }

      const campaign: Campaign = {
        name: campaignName.trim(),
        products: selectedProducts,
        companyId: currentUser.companyId,
        createdAt: new Date().toISOString(),
        status: 'active',
        sponsored: sponsorshipConfig.sponsored,
        sponsorshipStartDate: sponsorshipConfig.sponsorshipStartDate,
        sponsorshipEndDate: sponsorshipConfig.sponsorshipEndDate,
        pricingType: sponsorshipConfig.pricingType,
        pricingValue: sponsorshipConfig.pricingValue,
        nextPaymentDate: getNextFriday().toISOString()
      };

      // Criar a campanha
      const docRef = await addDoc(collection(db, 'campaigns'), campaign);
      
      // Atualizar os produtos selecionados
      const batch = writeBatch(db);
      for (const productId of selectedProducts) {
        const productRef = doc(db, 'products', productId);
        batch.update(productRef, {
          sponsored: true,
          sponsorshipStartDate: campaign.sponsorshipStartDate,
          sponsorshipEndDate: campaign.sponsorshipEndDate,
          campaignId: docRef.id
        });
      }
      await batch.commit();

      // Inicializar coleções
      await initializeCampaignCollections(docRef.id);

      // Limpar estado
      setShowCreateFlow(false);
      setStep(1);
      setCampaignName('');
      setSelectedProducts([]);
      setSponsorshipConfig(null);
    } catch (err) {
      console.error('Erro ao criar campanha:', err);
      if (err instanceof Error) {
        setError(`Erro ao criar campanha: ${err.message}`);
      } else {
        setError('Erro ao criar campanha. Por favor, tente novamente mais tarde.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const handleNext = () => {
    setError('');

    if (step === 1) {
      if (!campaignName.trim()) {
        setError('Por favor, insira um nome para a campanha');
        return;
      }
      setStep(2);
    } 
    else if (step === 2) {
      if (selectedProducts.length === 0) {
        setError('Por favor, selecione pelo menos um produto');
        return;
      }
      setStep(3);
    }
    else if (step === 3) {
      if (!sponsorshipConfig) {
        setError('Por favor, configure os detalhes do patrocínio corretamente');
        return;
      }
      handleCreateCampaign();
    }
  };

  const handleStartCreating = () => {
    setShowCreateFlow(true);
    setStep(1);
    setCampaignName('');
    setSelectedProducts([]);
    setSponsorshipConfig(null);
  };

  const handleUpdateProducts = async (campaignId: string, newProducts: string[]) => {
    if (!campaignId) return;
    
    try {
      setIsUpdating(true);
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        products: newProducts
      });
      await fetchActiveCampaigns();
      setIsEditingProducts(false);
      setSelectedCampaign(null);
    } catch (err) {
      console.error('Erro ao atualizar produtos:', err);
      setError('Erro ao atualizar produtos da campanha');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePauseCampaign = async (campaignId: string) => {
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status: 'paused',
        sponsored: false, // Desativa o patrocínio quando a campanha é pausada
        pausedAt: new Date().toISOString()
      });
      // Não é necessário chamar fetchActiveCampaigns pois o onSnapshot atualizará automaticamente
    } catch (err) {
      console.error('Erro ao pausar campanha:', err);
      setError('Erro ao pausar campanha. Por favor, tente novamente mais tarde.');
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta campanha?')) {
      return;
    }

    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      
      if (!campaignSnap.exists()) {
        setError('Campanha não encontrada');
        return;
      }

      const campaign = campaignSnap.data();
      const productIds = campaign.products || [];

      // Primeiro batch: Atualizar produtos
      const batch1 = writeBatch(db);
      for (const productId of productIds) {
        const productRef = doc(db, 'products', productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          batch1.update(productRef, {
            sponsored: deleteField(),
            sponsorshipStartDate: deleteField(),
            sponsorshipEndDate: deleteField()
          });
        }
      }
      await batch1.commit();

      // Segundo batch: Atualizar campanha
      const batch2 = writeBatch(db);
      batch2.update(campaignRef, {
        status: 'deleted',
        sponsored: false,
        sponsorshipStartDate: null,
        sponsorshipEndDate: null,
        deletedAt: new Date().toISOString()
      });
      await batch2.commit();

      // Limpar subcoleções
      const subcollections = ['views', 'clicks', 'sales', 'payments'];
      for (const subcollectionName of subcollections) {
        const subcollectionRef = collection(db, `campaigns/${campaignId}/${subcollectionName}`);
        const subcollectionDocs = await getDocs(subcollectionRef);
        
        if (!subcollectionDocs.empty) {
          const batch3 = writeBatch(db);
          subcollectionDocs.forEach((doc) => {
            batch3.delete(doc.ref);
          });
          await batch3.commit();
        }
      }

      // Não é necessário chamar fetchActiveCampaigns pois o onSnapshot atualizará automaticamente
    } catch (err) {
      console.error('Erro ao excluir campanha:', err);
      setError('Erro ao excluir campanha. Por favor, tente novamente mais tarde.');
    }
  };

  const handleReactivateCampaign = async (campaignId: string) => {
    setIsUpdating(true);
    try {
      const campaignRef = doc(db, 'campaigns', campaignId);
      await updateDoc(campaignRef, {
        status: 'active',
        pausedReason: null,
        reactivatedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      // Não é necessário chamar fetchActiveCampaigns pois o onSnapshot atualizará automaticamente
    } catch (err) {
      console.error('Erro ao reativar campanha:', err);
      setError('Erro ao reativar campanha. Por favor, tente novamente mais tarde.');
    } finally {
      setIsUpdating(false);
    }
  };

  const generateTrackingCode = async () => {
    try {
      const code = `
<!-- Código de Rastreamento de Campanhas -->
<script type="module">
  // Import Firebase
  import { initializeApp } from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-app.js';
  import { getFirestore, collection, addDoc, query, where, getDocs } from 'https://www.gstatic.com/firebasejs/10.7.1/firebase-firestore.js';

  // Configuração do Firebase
  const firebaseConfig = {
    apiKey: "AIzaSyBxcYs6TKSzPwC8-iXR_bxhxQhxq2EpJvg",
    authDomain: "etc-controlo.firebaseapp.com",
    projectId: "etc-controlo",
    storageBucket: "etc-controlo.appspot.com",
    messagingSenderId: "1031859158148",
    appId: "1:1031859158148:web:9e7a5f4f4d21f8c5d2c8df"
  };

  // Inicializar Firebase
  const app = initializeApp(firebaseConfig);
  const db = getFirestore(app);

  // Função para buscar campanhas ativas
  async function getActiveCampaigns() {
    const campaignsRef = collection(db, 'campaigns');
    const q = query(campaignsRef, where('status', '==', 'active'));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  }
  
  // Função para registrar visualização
  async function registerView() {
    try {
      const campaigns = await getActiveCampaigns();
      for (const campaign of campaigns) {
        const viewsRef = collection(db, 'campaigns', campaign.id, 'views');
        await addDoc(viewsRef, {
          timestamp: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Erro ao registrar visualização:', error);
    }
  }

  // Função para registrar clique
  async function registerClick(productId) {
    try {
      const campaigns = await getActiveCampaigns();
      // Filtrar campanhas que contêm o produto
      const relevantCampaigns = campaigns.filter(campaign => 
        campaign.products?.includes(productId)
      );

      for (const campaign of relevantCampaigns) {
        const clicksRef = collection(db, 'campaigns', campaign.id, 'clicks');
        await addDoc(clicksRef, {
          timestamp: new Date().toISOString(),
          productId: productId
        });
      }
    } catch (error) {
      console.error('Erro ao registrar clique:', error);
    }
  }

  // Função para registrar venda
  async function registerSale(productId, value) {
    try {
      const campaigns = await getActiveCampaigns();
      // Filtrar campanhas que contêm o produto
      const relevantCampaigns = campaigns.filter(campaign => 
        campaign.products?.includes(productId)
      );

      for (const campaign of relevantCampaigns) {
        const salesRef = collection(db, 'campaigns', campaign.id, 'sales');
        await addDoc(salesRef, {
          timestamp: new Date().toISOString(),
          productId: productId,
          value: value
        });
      }
    } catch (error) {
      console.error('Erro ao registrar venda:', error);
    }
  }

  // Registrar visualização ao carregar
  window.addEventListener('load', registerView);

  // Adicionar listeners para cliques nos produtos
  document.querySelectorAll('[data-product-id]').forEach(element => {
    element.addEventListener('click', function(e) {
      const productId = this.getAttribute('data-product-id');
      if (productId) {
        registerClick(productId);
      }
    });
  });

  // Expor função de registro de venda globalmente
  window.registerAdSale = registerSale;
</script>`;

      setTrackingCode(code);
      setShowCodeModal(true);
    } catch (err) {
      console.error('Erro ao gerar código:', err);
      setError('Erro ao gerar código de rastreamento.');
    }
  };

  const copyCodeToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(trackingCode);
      // You can add a toast notification here if you want
      console.log('Código copiado com sucesso!');
    } catch (err) {
      console.error('Erro ao copiar código:', err);
    }
  };

  const ErrorMessage = () => {
    if (!error) return null;

    return (
      <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center justify-between">
        <div className="flex items-center gap-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{error}</span>
        </div>
      </div>
    );
  };

  const formatCurrency = (value: number) => {
    return `€ ${value.toFixed(2)}`;
  };

  useEffect(() => {
    const checkPaymentMethod = async () => {
      if (!currentUser?.companyId) return;

      try {
        const companyRef = doc(db, 'companies', currentUser.companyId);
        const companySnap = await getDoc(companyRef);
        
        if (companySnap.exists()) {
          const data = companySnap.data();
          setHasPaymentMethod(!!data.stripeCustomerId && !!data.defaultPaymentMethod);
        } else {
          setHasPaymentMethod(false);
        }
        setLoadingStripe(false);
      } catch (err) {
        console.error('Erro ao verificar método de pagamento:', err);
        setHasPaymentMethod(false);
        setLoadingStripe(false);
      }
    };

    checkPaymentMethod();
  }, [currentUser?.companyId]);

  return (
    <div className="p-6">
      {!hasPaymentMethod && !loadingStripe && !showCreateFlow && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span>Cartão de crédito inválido ou não configurado. Para criar campanhas publicitárias, é necessário configurar um cartão de crédito.</span>
          </div>
          <a 
            href="/dashboard/settings/payments" 
            className="ml-4 px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors whitespace-nowrap"
          >
            Configurar Cartão
          </a>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Publicidade</h1>
        <div className="flex items-center gap-3">
          {(currentUser?.role === 'admin' || currentUser?.isAdmin) && !showCreateFlow && (
            <button
              onClick={generateTrackingCode}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors"
              title="Gerar Código de Rastreamento"
            >
              <Copy size={20} />
              Copiar Código
            </button>
          )}
          {!showCreateFlow && (
            <button
              onClick={() => {
                if (!hasPaymentMethod) {
                  setError('É necessário configurar um cartão de crédito em Configurações > Pagamentos antes de criar uma campanha.');
                  return;
                }
                setShowCreateFlow(true);
                setStep(1);
                setCampaignName('');
                setSelectedProducts([]);
                setSponsorshipConfig(null);
                setError('');
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                hasPaymentMethod 
                  ? 'bg-primary text-white hover:bg-primary/90' 
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
              disabled={!hasPaymentMethod}
            >
              <PlusCircle size={20} />
              Nova Campanha
            </button>
          )}
        </div>
      </div>

      {showCreateFlow ? (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold">Criar Nova Campanha</h2>
            <button
              onClick={() => setShowCreateFlow(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancelar
            </button>
          </div>
          
          <div className="space-y-8">
            {/* Progress Steps */}
            <div className="flex justify-between mb-8">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`flex items-center ${s !== 3 ? 'flex-1' : ''}`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      s <= step ? 'bg-primary text-white' : 'bg-gray-200'
                    }`}
                  >
                    {s}
                  </div>
                  {s !== 3 && (
                    <div
                      className={`flex-1 h-1 mx-2 ${
                        s < step ? 'bg-primary' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>

            {/* Step Content */}
            {step === 1 && (
              <div>
                <h3 className="text-lg font-medium mb-4">Nome da Campanha</h3>
                <input
                  type="text"
                  value={campaignName}
                  onChange={(e) => setCampaignName(e.target.value)}
                  placeholder="Digite o nome da campanha"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            {step === 2 && (
              <div>
                <h3 className="text-lg font-medium mb-4">Selecionar Produtos</h3>
                <p className="text-gray-600 mb-4">
                  Selecione os produtos que deseja incluir na campanha.
                </p>
                <ProductSelector
                  selectedProducts={selectedProducts}
                  onProductSelect={setSelectedProducts}
                />
              </div>
            )}

            {step === 3 && (
              <div>
                <h3 className="text-lg font-medium mb-4">Configurar Patrocínio</h3>
                <SponsorshipConfig onConfigChange={setSponsorshipConfig} />
              </div>
            )}

            {error && (
              <div className="text-red-500 text-sm mt-4">
                {error}
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              {step > 1 && (
                <button
                  onClick={() => setStep(step - 1)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Voltar
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={isCreating}
                className={`px-6 py-2 bg-primary text-white rounded-md hover:bg-primary/90 transition-colors ${
                  isCreating ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {step === 3 
                  ? (isCreating ? 'Criando...' : 'Criar Campanha') 
                  : 'Próximo'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* Date Filter */}
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-medium">Filtrar por período:</span>
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Data Inicial</label>
                  <input
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">Data Final</label>
                  <input
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    className="px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Campaigns List */}
          <div className="grid gap-6 mt-6">
            {activeCampaigns.map((campaign) => (
              <div key={campaign.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold">{campaign.name}</h3>
                    <p className="text-sm text-gray-500">Criada em: {new Date(campaign.createdAt).toLocaleDateString('pt-PT')}</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedCampaignForStats(campaign.id)}
                      className="px-3 py-1 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center gap-1"
                      title="Ver estatísticas por produto"
                    >
                      <BarChart size={16} />
                      Estatísticas por Produto
                    </button>
                    {campaign.status === 'active' ? (
                      <button
                        onClick={() => handlePauseCampaign(campaign.id!)}
                        className="px-3 py-1.5 text-sm text-yellow-700 bg-yellow-50 rounded-md hover:bg-yellow-100"
                        disabled={isUpdating}
                      >
                        Pausar
                      </button>
                    ) : (
                      <button
                        onClick={() => handleReactivateCampaign(campaign.id!)}
                        className="px-3 py-1.5 text-sm text-green-700 bg-green-50 rounded-md hover:bg-green-100"
                        disabled={isUpdating}
                      >
                        Reativar
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteCampaign(campaign.id!)}
                      className="px-3 py-1.5 text-sm text-red-700 bg-red-50 rounded-md hover:bg-red-100"
                      disabled={isUpdating}
                    >
                      Apagar
                    </button>
                  </div>
                </div>

                {/* Campaign Stats */}
                <div className="grid grid-cols-6 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Visualizações</p>
                    <p className="text-xl font-semibold text-gray-900">{campaign.stats?.views || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Cliques</p>
                    <p className="text-xl font-semibold text-gray-900">{campaign.stats?.clicks || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Vendas</p>
                    <p className="text-xl font-semibold text-gray-900">{campaign.stats?.sales || 0}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Custo Total</p>
                    <p className="text-xl font-semibold text-gray-900">{formatCurrency(campaign.stats?.cost || 0)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Valor já Pago</p>
                    <p className="text-xl font-semibold text-gray-900">{formatCurrency(campaign.stats?.paidAmount || 0)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm font-medium text-gray-600 mb-1">Próximo Pagamento</p>
                    <p className="text-xl font-semibold text-gray-900">{new Date(campaign.stats?.nextPaymentDate || '').toLocaleDateString('pt-BR')}</p>
                    <p className="text-sm text-gray-500 mt-1">{formatCurrency(campaign.stats?.amountToPay || 0)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Code Copy Modal */}
      {showCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-[600px] max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">Código de Rastreamento</h3>
              <button
                onClick={() => setShowCodeModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-4 overflow-auto flex-1">
              <pre className="bg-gray-50 p-3 rounded text-sm font-mono whitespace-pre-wrap break-all">
                {trackingCode}
              </pre>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
              <button
                onClick={copyCodeToClipboard}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition-colors"
              >
                <Copy size={16} />
                Copiar Código
              </button>
              <button
                onClick={() => setShowCodeModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de estatísticas por produto */}
      {selectedCampaignForStats && (
        <ProductStats
          campaignId={selectedCampaignForStats}
          onClose={() => setSelectedCampaignForStats(null)}
        />
      )}
    </div>
  );
}
